/**
 * The table: Pixi scene (`scene/PixiHost`) under the DOM overlays (HUD, subtitle, PIP strip).
 * The scene publishes anchors + hudSafe insets through its ports into the store. If Pixi fails to
 * mount (no WebGL), a plain placeholder keeps the shell usable.
 *
 * `PixiHost` is the shell's only import of `scene/`, and it is lazy on purpose (C-T25): a static
 * import pulls all of Pixi into the entry chunk, which every player downloads before the Boot
 * screen can paint even though the scene is not needed until they reach a table. Loading it here
 * splits ~500 kB out of first-interactive and starts fetching it while the lobby is on screen.
 *
 * `onReady` builds the table itself (`installTableScene`, C-T12/C-T13) and hands it the four
 * things `scene/**` cannot reach for on its own: the public view, my private view, a way to send
 * an intent, and the cue queue. `scene/**` never imports the store — zustand pulls React through
 * it — so this is the seam. The import is dynamic for the same reason `PixiHost` is: a static one
 * would drag the whole scene graph back onto the boot path.
 */
import { Component, type CSSProperties, type ErrorInfo, type ReactNode, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cueQueue } from '../../net/cues';
import { sendIntent, setSnapshotHydrator } from '../../net/wire';
import type { PixiContext } from '../../scene/host';
import type { ScenePorts } from '../../scene/SceneRoot';
import { shallow } from 'zustand/shallow';
import { store, useStore } from '../../store/store';
import { Hud } from '../overlays/Hud';
import { PipView } from '../overlays/PipView';
import { BidConfirm } from '../overlays/BidConfirm';
import { BlindNilPrompt } from '../overlays/BlindNilPrompt';
import { Spectator } from '../overlays/Spectator';
import { Subtitle } from '../overlays/Subtitle';
import { toast } from '../ui';

const PixiHost = lazy(() => import('../../scene/PixiHost').then((m) => ({ default: m.PixiHost })));

/**
 * Warm the scene's code chunks without rendering them; called once the shell has painted.
 *
 * `PixiHost` is the big one; `table/install` is the chunk that builds the scene and used to be
 * fetched only when someone actually reached a table. Both are code, and both are safe here.
 *
 * The `table` *asset* bundle is deliberately NOT warmed here — see `preloadTableArt`, which the
 * lobby calls. Doing it from this hook competed with the boot bundle that "Deal me in" waits on and
 * left the Boot screen's button disabled long enough for a spec to time out on it.
 */
export function preloadScene(): Promise<unknown> {
  return Promise.all([import('../../scene/PixiHost'), import('../../scene/table/install')]).catch((err: unknown) => {
    console.warn('[shell] scene preload failed', err);
  });
}

/**
 * Warm the table's art, from the lobby.
 *
 * The `table` bundle holds the diorama plates, the ledger and deck@2x — what the felt is made of.
 * `scene/assets/manifest.ts` defers it until after the scene's first rendered frame, on the
 * reasoning that a player can be looking at a dealt hand before it lands. On a cold production load
 * that is precisely what happens, and what they are looking at is an empty table: the art was still
 * arriving two seconds after the table screen appeared.
 *
 * The lobby is the right moment. The boot bundle is already in by then, so this competes with
 * nothing the player is waiting on, and they usually spend a few seconds there deciding — which is
 * exactly the time this needs.
 */
export function preloadTableArt(): Promise<unknown> {
  return import('../../scene/assets/manifest')
    .then((m) => m.loadTableBundle())
    .catch((err: unknown) => {
      console.warn('[shell] table art preload failed', err);
    });
}

/**
 * Has the art the table is drawn from arrived?
 *
 * Resolves immediately when the bundle is already in, so a warm load never shows the placeholder.
 * A failure resolves too: a missing texture costs a plainer table, and trapping the player behind a
 * spinner forever would be a far worse answer than letting them play with whatever loaded.
 */
export function whenTableArtReady(): Promise<void> {
  return import('../../scene/assets/manifest')
    .then((m) => m.loadTableBundle())
    .then(
      () => undefined,
      () => undefined,
    );
}

class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(err: Error, info: ErrorInfo) {
    console.warn('[shell] scene failed to mount', err, info.componentStack);
  }
  override render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

/**
 * Build the table scene once the Pixi host is up. Idempotent on the scene side, so React's
 * StrictMode double-mount and the host singleton both stay harmless.
 */
/** Returns a disposer for everything it subscribed; the caller owns it. */
async function buildTableScene(ctx: PixiContext): Promise<() => void> {
  const { installTableScene } = await import('../../scene/table/install');
  const e2e = new URLSearchParams(location.search).get('e2e') === '1';
  // The scene reads `getLayoutMode` only when it re-solves, so a Discord PIP/grid switch has to
  // poke it: without this the layout stays focused until something else resizes the host.
  //
  // This used to be deliberately dropped on the grounds that the scene outlives the screen. It
  // does, but the *subscription* did too: every remount added another, and each one called
  // `scene.resize()` on a single PIP toggle. Returned and disposed instead.
  const offLayoutMode = store.subscribe(
    (s) => s.presentation.layoutMode,
    () => ctx.scene.resize(),
  );
  await installTableScene(ctx, {
    getTable: () => store.getState().table,
    getPrivate: () => store.getState().privateView,
    // To whichever driver is wired to the store, not to the boot one: the tutorial runs its own
    // `ScriptedDriver` over the top and has to hear the player's bid and play (see `sendIntent`).
    send: sendIntent,
    cueQueue,
    setSubtitle: (subtitle) => store.getState().setPresentation({ subtitle }),
    subtitlesEnabled: () => store.getState().settings.subtitles,
    setDisplayedScores: (displayedScores) => store.getState().setPresentation({ displayedScores }),
    toast: (text) => toast(text, 'error'),
    // --- diorama/seats/foil agent: §4 blind nil prompt (review #6) ---
    setBlindNilOffer: (blindNilOffer) => store.getState().setPresentation({ blindNilOffer }),
      setBidLive: (bidLive) => store.getState().setPresentation({ bidLive }),
    // The Confirm button is DOM (`overlays/BidConfirm.tsx`) so it can be the shell's own gold
    // primary rather than a Pixi imitation of one; this is the data half of that seam.
    setBidConfirm: (bidConfirm) => store.getState().setPresentation({ bidConfirm }),
    // The player tapped their own avatar on the felt: open the emote picker. The HUD's 💬 button
    // is gone, so this is the route — `scene/**` cannot reach the store, and the avatar is Pixi.
    openEmotes: () => store.getState().setPresentation({ emotePickerOpen: true }),
    // --- end diorama/seats/foil agent ---
    // The tuple is freshly allocated on every read, so under zustand's default `Object.is` this
    // fired on *every* store write rather than only when a view moved — which is the amplifier the
    // scene's re-entrant bid subscription rode into a stack overflow in production. `shallow`
    // compares the two members instead, so it fires when a view actually changes.
    // A stable selector: fires only when the private view object changes, which is what makes
    // the bid re-offer safe to drive from a subscription at all.
    // A reconnect too old to replay arrives as a snapshot: rebuild rather than stay frozen.
    onSnapshot: (rebuild) => setSnapshotHydrator(rebuild),
    subscribePrivate: (cb) => store.subscribe((s) => s.privateView, cb),
    subscribeState: (cb) =>
      store.subscribe(
        (s) => [s.table, s.privateView] as const,
        ([table, priv]) => cb(table, priv),
        { fireImmediately: true, equalityFn: shallow },
      ),

    // --- audio (scene3): settings the engine cannot reach on its own -------------------------
    // `Settings.tsx` pushes slider changes through `window.__spades.audio`, which only exists
    // under `?e2e=1`, so in production every volume change wrote the store and nothing else.
    // These ports are the production route; `installAudio` reads them at install and on each fire.
    volumes: () => store.getState().settings.volume,
    subscribeVolumes: (cb) => store.subscribe((s) => s.settings.volume, cb),
    mutedEmotes: () => store.getState().settings.mutedEmotes,
    // --- end audio ---------------------------------------------------------------------------
  });

  // Under `?e2e=1` the real table gets the same probe `/dev/table` has, so a spec can assert on
  // what the scene actually rendered rather than on what the store holds. Production never loads
  // it: the import is inside the guard, so `testApi` stays out of the shipped path entirely.
  if (e2e) {
    const [{ extendTestApi, installTestApi }, { getTableScene }] = await Promise.all([
      import('../../scene/testApi'),
      import('../../scene/table/install'),
    ]);
    installTestApi(ctx.app, ctx.scene);
    const table = getTableScene();
    if (table) {
      extendTestApi({
        table,
        handCards: () => table.hand.cards.map((c) => c.card),
        trickCards: () => [...table.trick.played.entries()].map(([seat, v]) => ({ seat, card: v.card })),
      });
    }
  }

  // --- settings the scene cannot read for itself -------------------------------------------
  //
  // `scene/**` never imports the store, so anything the player sets has to be pushed across this
  // seam. Both of these were collected by Settings, written to the store, and read by nobody:
  // choosing "Reduced" restyled the DOM and left the canvas springing and shaking, and the graphics
  // override was pinned at whatever the governor last decided. Same shape of gap as the audio
  // volumes, same fix.
  const [{ setMotionPreference }, { bindPerfTier }, { selectEffectivePerfTier }] = await Promise.all([
    import('../../scene/juice/Motion'),
    import('../../scene/perf/tier'),
    import('../../store/selectors'),
  ]);

  setMotionPreference(store.getState().settings.motion);
  const offMotion = store.subscribe((st) => st.settings.motion, setMotionPreference);

  // `bindPerfTier` takes a subscribe function and hands it a setter, so the store selector and the
  // scene's setter meet without either side importing the other.
  const offPerf = bindPerfTier((set) => {
    set(selectEffectivePerfTier(store.getState()));
    return store.subscribe(selectEffectivePerfTier, set);
  });
  // --- end settings ---------------------------------------------------------------------------

  return () => {
    offLayoutMode();
    offMotion();
    offPerf();
  };
}

export function TableScreen() {
  const layoutMode = useStore((s) => s.presentation.layoutMode);
  const hudSafe = useStore((s) => s.presentation.hudSafe);
  /*
   * False until the table has actually painted.
   *
   * The signal is the scene's own `publishPainted`, which waits for its dressing to decode, for
   * real state, and then for one more ticker frame. Everything else the shell can see — the canvas
   * element, the scene's test api, `hudSafe`, the anchors — is published two seconds earlier on a
   * cold load, which is exactly how a complete HUD ended up drawn over an empty felt.
   *
   * The asset bundle is a *backstop*, not the gate: it resolves before the paint, so it must never
   * be allowed to drop the placeholder early. It only matters when no paint is coming at all — no
   * WebGL, a scene that threw, a driver with no table — where the alternative is a player stuck
   * behind a placeholder forever. The extra second is deliberate: it must lose the race whenever a
   * real paint is on its way.
   */
  const scenePainted = useStore((s) => s.presentation.scenePainted);
  const [bailedOut, setBailedOut] = useState(false);
  const artReady = scenePainted || bailedOut;

  useEffect(() => {
    // A fresh mount is a fresh table: a rejoin has to paint again before the HUD comes back.
    store.getState().setPresentation({ scenePainted: false });
    setBailedOut(false);
    let alive = true;
    void whenTableArtReady().then(() => {
      const t = setTimeout(() => alive && setBailedOut(true), 1000);
      return () => clearTimeout(t);
    });
    return () => {
      alive = false;
    };
  }, []);
  // The disposer arrives asynchronously, so unmount may beat it; `disposeRef` covers both orders.
  const disposeRef = useRef<(() => void) | null>(null);
  const unmountedRef = useRef(false);
  const onReady = useCallback((ctx: PixiContext) => {
    void buildTableScene(ctx).then(
      (dispose) => {
        if (unmountedRef.current) dispose();
        else disposeRef.current = dispose;
      },
      (err: unknown) => console.warn('[shell] table scene failed', err),
    );
  }, []);
  useEffect(
    () => () => {
      unmountedRef.current = true;
      disposeRef.current?.();
      disposeRef.current = null;
    },
    [],
  );
  const ports = useMemo<ScenePorts>(
    () => ({
      publishAnchors: (anchors) => store.getState().setPresentation({ anchors }),
      publishPainted: () => store.getState().setPresentation({ scenePainted: true }),
      publishLayout: (layout) => store.getState().setPresentation({ hudSafe: { ...layout.hudSafe } }),
      getLayoutMode: () => store.getState().presentation.layoutMode,
      snapResize: new URLSearchParams(location.search).get('e2e') === '1',
    }),
    [],
  );

  // The scene's measured clearance, published as CSS custom properties on the screen root.
  //
  // It belongs here rather than on the HUD because every overlay below is positioned against it and
  // custom properties only inherit downwards. While the HUD owned them, `Subtitle`, `BlindNilPrompt`
  // and `Spectator` were its siblings, saw nothing, and fell back to the raw device safe area — which
  // in portrait put an announcer bark on top of the player's own cards.
  const overlayInsets: CSSProperties = hudSafe
    ? {
        ['--hud-top' as string]: `${hudSafe.top}px`,
        ['--hud-bottom' as string]: `${hudSafe.bottom}px`,
        ['--hud-left' as string]: `${hudSafe.left}px`,
        ['--hud-right' as string]: `${hudSafe.right}px`,
        ['--hud-corner' as string]: `${hudSafe.corner}px`,
      }
    : {};

  return (
    <div className="table-screen" style={overlayInsets} data-testid="screen-table">
      {/* The placeholder is the felt itself, so a failed or still-loading scene looks like an
          empty table rather than a hole. */}
      <div className="table-area" data-testid="table-area">
        <SceneBoundary>
          <Suspense fallback={null}>
            <PixiHost ports={ports} onReady={onReady} />
          </Suspense>
        </SceneBoundary>
      </div>
      {/* Until the felt has something on it, say so and hold the HUD back.
          A complete HUD over an empty canvas does not read as loading, it reads as broken: every
          number and every control is there, drawn over nothing, with no sign that anything is still
          coming. The placeholder is the felt colour rather than a spinner on a void, so the arrival
          of the real table is a fill rather than a scene change. */}
      {!artReady && (
        <div className="table-loading" data-testid="table-loading" role="status" aria-live="polite">
          <span className="table-loading__label">Dealing you in…</span>
        </div>
      )}
      {/* PIP is a 320x180 thumbnail beside a video call: the strip is the entire UI. The HUD (and
          with it every sheet, which renders from inside it) and the announcer subtitle are both
          suppressed — there is no room, and nothing in PIP is interactive anyway. */}
      {layoutMode === 'pip' ? (
        <PipView />
      ) : (
        artReady && (
          <>
            <Hud />
            <Spectator />
            <BlindNilPrompt />
            <BidConfirm />
            <Subtitle />
          </>
        )
      )}
    </div>
  );
}
